import * as React from "react";
import { createPortal } from "react-dom";

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Extra class on the root `.vfm-sheet` — e.g. to raise its z-index when the
   * sheet is opened from within another high-z-index modal. */
  className?: string;
  children: React.ReactNode;
}

/**
 * Bottom-anchored modal sheet (Field Day styled). Slides up from the bottom,
 * closes on scrim tap or Escape, and locks body scroll while open. Portaled to
 * <body> so it escapes any ancestor overflow/stacking context. Mobile-only
 * primitive — only mounted inside mobile components, which only render < 768px.
 */
export function BottomSheet({
  open,
  onClose,
  title,
  className,
  children,
}: BottomSheetProps): React.ReactElement | null {
  // Keep the latest onClose without making it an effect dependency. Call sites
  // pass an inline arrow, so a new reference on every parent render (e.g. when
  // useMe() resolves) would otherwise re-run this effect and momentarily
  // restore — i.e. unlock — body scroll while the sheet is still open.
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  React.useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCloseRef.current();
    }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div
      className={`vfm-sheet${className ? ` ${className}` : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button type="button" className="vfm-sheet__scrim" aria-label="Close" onClick={onClose} />
      <div className="vfm-sheet__panel v-field">
        <div className="vfm-sheet__grip" aria-hidden="true" />
        <div className="vfm-sheet__header">
          {title && <div className="vfm-sheet__title">{title}</div>}
          <button type="button" className="vfm-sheet__close" aria-label="Close" onClick={onClose}>
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
